import { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Modal from "components/Modal";
import type { AppDispatch } from "store";
import type { IndicatorName, IndicatorSettings } from "../../model";
import {
  selectIndicatorsInstances,
  updateIndicatorSettings,
  INDICATOR_DEFAULT_SETTINGS,
} from "../../model";
import { IndicatorTabContent } from "./IndicatorTabContent";
import { IndicatorSettingsFooter } from "./IndicatorSettingsFooter";
import styles from "./IndicatorSettingsModal.module.css";

type Tab = "inputs" | "styles";

interface Props {
  instanceId: string;
  indicatorName: IndicatorName;
  label: string;
  onClose: () => void;
}

export const IndicatorSettingsModal: FC<Props> = ({ instanceId, indicatorName, label, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [activeTab, setActiveTab] = useState<Tab>("inputs");

  const instances = useSelector(selectIndicatorsInstances);
  const instance = instances.find((i) => i.id === instanceId);
  const isVisible = instance?.visible !== false;

  const [settings, setSettings] = useState<IndicatorSettings | undefined>(
    instance?.settings ?? INDICATOR_DEFAULT_SETTINGS[indicatorName],
  );

  const handleSettingsChange = (updated: IndicatorSettings) => {
    setSettings(updated);
    dispatch(updateIndicatorSettings({ id: instanceId, settings: updated }));
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={label}>
      <div className={styles.content}>
        <div className={styles.tabBar}>
          {(["inputs", "styles"] as Tab[]).map((tab) => (
            <button
              key={tab}
              className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className={styles.tabContent}>
          {settings && (
            <IndicatorTabContent
              activeTab={activeTab}
              indicatorName={indicatorName}
              settings={settings}
              onChange={handleSettingsChange}
            />
          )}
        </div>

        <IndicatorSettingsFooter
          instanceId={instanceId}
          isVisible={isVisible}
          onClose={onClose}
        />
      </div>
    </Modal>
  );
};
